import { useEffect, useState } from 'react';
import type { FormsSnapshot } from './types';

let cache: Promise<FormsSnapshot> | null = null;

function fetchSnapshot(): Promise<FormsSnapshot> {
  return (cache ??= fetch('/data/forms.json', { cache: 'no-cache' }).then((res) => {
    if (!res.ok) throw new Error(`Could not load forms (HTTP ${res.status})`);
    return res.json() as Promise<FormsSnapshot>;
  }));
}

/** Drop the cached snapshot (used by the retry button on error states). */
export function invalidateSnapshotCache(): void {
  cache = null;
}

interface SnapshotState {
  snapshot: FormsSnapshot | null;
  error: string | null;
  loading: boolean;
}

export function useSnapshot(): SnapshotState & { retry: () => void } {
  const [state, setState] = useState<SnapshotState>({ snapshot: null, error: null, loading: true });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchSnapshot()
      .then((snapshot) => !cancelled && setState({ snapshot, error: null, loading: false }))
      .catch((err: Error) => !cancelled && setState({ snapshot: null, error: err.message, loading: false }));
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return {
    ...state,
    retry: () => {
      invalidateSnapshotCache();
      setAttempt((a) => a + 1);
    },
  };
}
